import axios from 'axios';
import userNotificationSettingsService from '@/services/user-notification-settings.service';

const initialStatus = () => ({
  error: null,
  pending: false,
  resolved: false,
  rejected: false,
});

const initialState = () => ({
  userNotificationSettings: [],
  status: initialStatus(),
  marketSummarySections: [],
  marketSummarySectionsStatus: initialStatus(),
});

const state = initialState();

const actions = {
  getUserNotificationSettings({ commit }) {
    commit('GET_USER_NOTIFICATION_SETTINGS');
    return userNotificationSettingsService.getUserNotificationSettings().then(
      (userNotificationSettings) => commit('GET_USER_NOTIFICATION_SETTINGS_SUCCESS', userNotificationSettings),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('GET_USER_NOTIFICATION_SETTINGS_ERROR', error);
        }
      },
    );
  },
  // Partial update: pass only the channel flag(s) that changed (`isEmailSubscribed`, `isInAppSubscribed`).
  editUserNotificationSetting({ commit }, { userNotificationSettingId, isEmailSubscribed, isInAppSubscribed }) {
    commit('EDIT_USER_NOTIFICATION_SETTING');
    return userNotificationSettingsService
      .editUserNotificationSetting({ userNotificationSettingId, isEmailSubscribed, isInAppSubscribed })
      .then(
        (editedUserNotificationSetting) =>
          commit('EDIT_USER_NOTIFICATION_SETTING_SUCCESS', editedUserNotificationSetting),
        (error) => {
          if (!axios.isCancel(error)) {
            commit('EDIT_USER_NOTIFICATION_SETTING_ERROR', error);
          }
          throw error;
        },
      );
  },
  addUserNotificationSetting({ commit }, userNotificationSetting) {
    commit('CREATE_USER_NOTIFICATION_SETTING');
    return userNotificationSettingsService.addUserNotificationSetting(userNotificationSetting).then(
      (createdUserNotificationSetting) =>
        commit('CREATE_USER_NOTIFICATION_SETTING_SUCCESS', createdUserNotificationSetting),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('CREATE_USER_NOTIFICATION_SETTING_ERROR', error);
        }
        throw error;
      },
    );
  },
  // Creates the rows of several types in one request. Rethrows so the caller can roll back its optimistic state.
  addUserNotificationSettings({ commit }, userNotificationSettings) {
    commit('CREATE_USER_NOTIFICATION_SETTINGS');
    return userNotificationSettingsService.addUserNotificationSettings(userNotificationSettings).then(
      (createdUserNotificationSettings) =>
        commit('CREATE_USER_NOTIFICATION_SETTINGS_SUCCESS', createdUserNotificationSettings),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('CREATE_USER_NOTIFICATION_SETTINGS_ERROR', error);
        }
        throw error;
      },
    );
  },
  deleteUserNotificationSetting({ commit }, userNotificationSettingId) {
    commit('DELETE_USER_NOTIFICATION_SETTING');
    return userNotificationSettingsService.deleteUserNotificationSetting(userNotificationSettingId).then(
      (deletedUserNotificationSetting) =>
        commit('DELETE_USER_NOTIFICATION_SETTING_SUCCESS', deletedUserNotificationSetting),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('DELETE_USER_NOTIFICATION_SETTING_ERROR', error);
        }
      },
    );
  },
  getMarketSummarySections({ commit }) {
    commit('GET_MARKET_SUMMARY_SECTIONS');
    return userNotificationSettingsService.getMarketSummarySections().then(
      (sections) => commit('GET_MARKET_SUMMARY_SECTIONS_SUCCESS', sections),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('GET_MARKET_SUMMARY_SECTIONS_ERROR', error);
        }
      },
    );
  },
  // Replaces the whole set. Rethrows so the caller can roll back its optimistic state.
  updateMarketSummarySections({ commit }, sections) {
    commit('UPDATE_MARKET_SUMMARY_SECTIONS');
    return userNotificationSettingsService.updateMarketSummarySections(sections).then(
      (updatedSections) => commit('UPDATE_MARKET_SUMMARY_SECTIONS_SUCCESS', updatedSections),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('UPDATE_MARKET_SUMMARY_SECTIONS_ERROR', error);
        }
        throw error;
      },
    );
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const mutations = {
  GET_USER_NOTIFICATION_SETTINGS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.userNotificationSettings = [];
  },
  GET_USER_NOTIFICATION_SETTINGS_SUCCESS(state, userNotificationSettings) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.userNotificationSettings = userNotificationSettings;
  },
  GET_USER_NOTIFICATION_SETTINGS_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  EDIT_USER_NOTIFICATION_SETTING(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  EDIT_USER_NOTIFICATION_SETTING_SUCCESS(state, userNotificationSetting) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.userNotificationSettings = state.userNotificationSettings.map((setting) => {
      if (setting.id === userNotificationSetting.id) {
        return userNotificationSetting;
      }
      return setting;
    });
  },
  EDIT_USER_NOTIFICATION_SETTING_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  CREATE_USER_NOTIFICATION_SETTING(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CREATE_USER_NOTIFICATION_SETTING_SUCCESS(state, userNotificationSetting) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.userNotificationSettings.unshift(userNotificationSetting);
  },
  CREATE_USER_NOTIFICATION_SETTING_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  CREATE_USER_NOTIFICATION_SETTINGS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  // The batch returns a row the user already had as stored: it replaces the known one instead of duplicating it.
  CREATE_USER_NOTIFICATION_SETTINGS_SUCCESS(state, userNotificationSettings) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const ids = userNotificationSettings.map((setting) => setting.id);
    state.userNotificationSettings = [
      ...userNotificationSettings,
      ...state.userNotificationSettings.filter((setting) => !ids.includes(setting.id)),
    ];
  },
  CREATE_USER_NOTIFICATION_SETTINGS_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  DELETE_USER_NOTIFICATION_SETTING(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_USER_NOTIFICATION_SETTING_SUCCESS(state, userNotificationSetting) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.userNotificationSettings = state.userNotificationSettings.filter(
      (setting) => setting.id !== userNotificationSetting.id,
    );
  },
  DELETE_USER_NOTIFICATION_SETTING_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  GET_MARKET_SUMMARY_SECTIONS(state) {
    state.marketSummarySectionsStatus = { ...initialStatus(), pending: true };
    state.marketSummarySections = [];
  },
  GET_MARKET_SUMMARY_SECTIONS_SUCCESS(state, sections) {
    state.marketSummarySectionsStatus = { ...state.marketSummarySectionsStatus, pending: false, resolved: true };
    state.marketSummarySections = sections;
  },
  GET_MARKET_SUMMARY_SECTIONS_ERROR(state, error) {
    state.marketSummarySectionsStatus = { ...state.marketSummarySectionsStatus, pending: false, rejected: true, error };
  },
  UPDATE_MARKET_SUMMARY_SECTIONS(state) {
    state.marketSummarySectionsStatus = { ...initialStatus(), pending: true };
  },
  UPDATE_MARKET_SUMMARY_SECTIONS_SUCCESS(state, sections) {
    state.marketSummarySectionsStatus = { ...state.marketSummarySectionsStatus, pending: false, resolved: true };
    state.marketSummarySections = sections;
  },
  UPDATE_MARKET_SUMMARY_SECTIONS_ERROR(state, error) {
    state.marketSummarySectionsStatus = { ...state.marketSummarySectionsStatus, pending: false, rejected: true, error };
  },
};

const getters = {
  userNotificationSettings: (state) => state.userNotificationSettings,
  marketSummarySections: (state) => state.marketSummarySections,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
  marketSummaryPending: (state) => state.marketSummarySectionsStatus.pending,
  marketSummaryRejected: (state) => state.marketSummarySectionsStatus.rejected,
};

export const userNotificationSettings = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
